import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCertero } from "@/context/CerteroContext";
import { NAV_LINKS, CONTACTO, track, waLink } from "@/data/content";
import { EASE } from "@/components/Reveal";

export default function Navbar() {
  const { openLead, scrollToId } = useCertero();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    track("nav_click", { section: id });
    if (pathname !== "/") {
      navigate(`/#${id}`);
      if (id === "llave-en-mano") setTimeout(() => window.dispatchEvent(new CustomEvent("certero:abrir-desplegable", { detail: { bloque: "llave-en-mano" } })), 900);
      return;
    }
    setTimeout(() => {
      scrollToId(id);
      // «Llave en mano» baja al bloque y abre su desplegable automáticamente.
      if (id === "llave-en-mano") window.dispatchEvent(new CustomEvent("certero:abrir-desplegable", { detail: { bloque: "llave-en-mano" } }));
    }, open ? 250 : 0);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled || open
          ? "bg-abyss/90 backdrop-blur-md border-b border-hud"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-[1400px] items-center justify-between px-4 sm:px-8 lg:px-12">
        <button
          data-testid="nav-logo"
          onClick={() => go("inicio")}
          className="flex items-center gap-3 text-left"
          aria-label="CERTERO SRL — inicio"
        >
          <img src="/logo-isotipo.svg" alt="Isotipo CERTERO SRL" className="h-9 w-auto" width="32" height="36" />
          <span className="hidden sm:block leading-none">
            <span className="font-display text-lg font-extrabold tracking-wide text-mist">CERTERO <span className="font-normal">SRL</span></span>
            <span className="mt-1 block font-mono text-[9px] uppercase tracking-[0.3em] text-certero">
              DJI Agriculture · Bolivia
            </span>
          </span>
        </button>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Secciones">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="group relative font-mono text-[11px] uppercase tracking-[0.2em] text-mist/70 transition-colors duration-300 hover:text-mist"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-certero transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid="nav-btn-whatsapp"
            href={waLink("Hola CERTERO SRL, quiero cotizar un dron agrícola.")}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("whatsapp_click", { origen: "nav" })}
            className="hidden h-10 w-10 items-center justify-center rounded-full border border-hud text-mist/80 transition-colors duration-300 hover:border-certero hover:text-certero md:flex"
            aria-label={`WhatsApp ${CONTACTO.whatsappDisplay}`}
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22c5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2Zm5.8 14.03c-.24.68-1.4 1.3-1.93 1.35-.53.05-1.03.24-2.9-.6-2.25-1-3.68-3.33-3.79-3.49-.11-.16-.9-1.25-.86-2.36.04-1.11.62-1.64.84-1.87.22-.23.48-.28.64-.28h.46c.15 0 .35-.06.54.41.2.48.67 1.65.73 1.77.06.12.1.26.02.42-.08.16-.15.26-.29.4-.14.14-.3.31-.42.42-.14.12-.29.26-.17.5.12.24.55.97 1.18 1.57.81.77 1.5 1.02 1.72 1.13.22.11.35.1.48-.06.13-.16.56-.65.71-.87.15-.22.3-.18.5-.11.2.07 1.29.61 1.51.72.22.11.37.17.42.26.05.09.05.53-.19 1.19Z"/></svg>
          </a>
          <button
            data-testid="nav-btn-cotizar"
            onClick={() => { track("cotizar_click", { origen: "nav" }); openLead("cotizacion"); }}
            className="hidden h-10 items-center rounded-full bg-certero px-5 font-display text-xs font-bold uppercase tracking-[0.15em] text-ink transition-[background-color,transform] duration-300 hover:bg-[#4bc443] sm:flex active:scale-95"
          >
            Cotizar
          </button>
          <button
            data-testid="nav-btn-menu"
            onClick={() => setOpen(!open)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-hud text-mist lg:hidden"
            aria-label={open ? "Cerrar menú" : "Abrir menú"}
            aria-expanded={open}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="overflow-hidden border-t border-hud bg-abyss/95 backdrop-blur-md lg:hidden"
            aria-label="Menú móvil"
          >
            <div className="flex flex-col px-6 py-4">
              {NAV_LINKS.map((l, i) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="flex items-center justify-between border-b border-hud/60 py-4 text-left"
                >
                  <span className="font-display text-base font-bold uppercase tracking-wide text-mist">{l.label}</span>
                  <span className="font-mono text-[10px] text-certero">0{i + 1}</span>
                </button>
              ))}
              <button
                data-testid="nav-mobile-btn-cotizar"
                onClick={() => { setOpen(false); openLead("cotizacion"); }}
                className="mt-5 mb-3 h-12 rounded-full bg-certero font-display text-sm font-bold uppercase tracking-[0.15em] text-ink"
              >
                Cotizar ahora
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
